import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Facebook, Instagram, Youtube } from "lucide-react";
import { toast } from "sonner";
import { api, formatError } from "../lib/api";
import LionLogo from "./LionLogo";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const { data } = await api.post("/newsletter", { email });
      toast.success(data.message);
      setEmail("");
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer className="border-t border-white/10 bg-[#070707] mt-24" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-16 grid grid-cols-1 md:grid-cols-4 gap-10">
        <div>
          <Link to="/" className="flex items-center gap-3 mb-4">
            <LionLogo size={44} />
            <div className="leading-tight">
              <div className="font-serif text-lg">Lyon's Den</div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#D4AF37]">Protection · LLC</div>
            </div>
          </Link>
          <p className="text-sm text-white/60 leading-relaxed">
            Shielded By Fire. Guarded By Spirit. Spiritual guidance, protection, and empowerment rooted in Southern Louisiana.
          </p>
        </div>

        <div>
          <div className="overline mb-4">Explore</div>
          <ul className="space-y-2 text-sm text-white/70">
            <li><Link to="/services" className="hover:text-[#D4AF37]">Services</Link></li>
            <li><Link to="/shop" className="hover:text-[#D4AF37]">Shop</Link></li>
            <li><Link to="/book-reading" className="hover:text-[#D4AF37]">Book a Reading</Link></li>
            <li><Link to="/house-calls" className="hover:text-[#D4AF37]">House Calls</Link></li>
            <li><Link to="/knowledge" className="hover:text-[#D4AF37]">Knowledge Center</Link></li>
          </ul>
        </div>

        <div>
          <div className="overline mb-4">Follow</div>
          <ul className="space-y-2 text-sm text-white/70">
            <li><a href="https://www.facebook.com/lyonsdenprotection" target="_blank" rel="noreferrer" className="flex items-center gap-2 hover:text-[#D4AF37]"><Facebook size={14}/>Facebook</a></li>
            <li><a href="https://www.instagram.com/lyonsdenprotection" target="_blank" rel="noreferrer" className="flex items-center gap-2 hover:text-[#D4AF37]"><Instagram size={14}/>Instagram</a></li>
            <li><a href="https://www.tiktok.com/@lyonsdenprotection" target="_blank" rel="noreferrer" className="flex items-center gap-2 hover:text-[#D4AF37]">TikTok</a></li>
            <li><a href="https://www.youtube.com/@LyonsDenProtection" target="_blank" rel="noreferrer" className="flex items-center gap-2 hover:text-[#D4AF37]"><Youtube size={14}/>YouTube</a></li>
            <li><a href="https://linktr.ee/LyonsDenProtection" target="_blank" rel="noreferrer" className="hover:text-[#D4AF37]">Linktree</a></li>
          </ul>
        </div>

        <div>
          <div className="overline mb-4">Newsletter</div>
          <p className="text-sm text-white/60 mb-3">Spiritual insight and seasonal offers, delivered with intention.</p>
          <form onSubmit={subscribe} className="flex flex-col gap-2" data-testid="footer-newsletter-form">
            <input
              type="email"
              required
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="bg-transparent border-b border-white/20 py-2 text-sm text-white placeholder:text-white/30 focus:border-[#D4AF37] outline-none"
              data-testid="footer-newsletter-email"
            />
            <button
              type="submit"
              disabled={loading}
              className="mt-2 self-start px-5 py-2 border border-[#D4AF37] text-[#D4AF37] text-xs uppercase tracking-[0.2em] hover:bg-[#D4AF37] hover:text-black transition-colors"
              data-testid="footer-newsletter-submit"
            >
              {loading ? "Joining…" : "Join the Den"}
            </button>
          </form>
        </div>
      </div>

      <div className="border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-6 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-white/40">
          <div>© {new Date().getFullYear()} Lyon's Den Protection, LLC. All rights reserved.</div>
          <div className="flex flex-wrap gap-x-4 gap-y-1 justify-center">
            <Link to="/refund-policy" className="hover:text-[#D4AF37]" data-testid="footer-refund-link">Refund</Link>
            <Link to="/cancellation-policy" className="hover:text-[#D4AF37]" data-testid="footer-cancellation-link">Cancellation</Link>
            <Link to="/terms" className="hover:text-[#D4AF37]" data-testid="footer-terms-link">Terms</Link>
            <Link to="/privacy" className="hover:text-[#D4AF37]" data-testid="footer-privacy-link">Privacy</Link>
            <Link to="/disclaimer" className="hover:text-[#D4AF37]" data-testid="footer-disclaimer-link">Disclaimer</Link>
            <Link to="/admin/login" className="hover:text-[#D4AF37]" data-testid="footer-admin-link">Admin</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
